/**
 * External dependencies
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { useAPI } from '../../../../app/api';
import { DEFAULT_LOTTIE_LIBRARY_PAGE_SIZE, mergeAnimationPages } from './utils';

const isCanceledRequest = (error) =>
  error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';

function useLottieLibrary() {
  const {
    actions: { getStudioAnimations },
  } = useAPI();
  const abortControllerRef = useRef();
  const lastRequestRef = useRef({
    nextSearchTerm: '',
    pageToken: 1,
    append: false,
  });
  const [animations, setAnimations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [nextPageToken, setNextPageToken] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  const abortCurrentRequest = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const fetchAnimations = useCallback(
    async ({ nextSearchTerm = '', pageToken = 1, append = false } = {}) => {
      if (!getStudioAnimations) {
        setAnimations([]);
        setHasMore(false);
        setNextPageToken(undefined);
        setTotalItems(0);
        setError(new Error('Studio animation API is unavailable.'));
        setIsLoaded(true);
        return;
      }

      abortCurrentRequest();
      lastRequestRef.current = { nextSearchTerm, pageToken, append };
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setIsLoading(true);
      setError(null);

      try {
        const result = await getStudioAnimations({
          searchTerm: nextSearchTerm,
          pageToken,
          pageSize: DEFAULT_LOTTIE_LIBRARY_PAGE_SIZE,
          signal: abortController.signal,
        });

        setAnimations((currentAnimations) =>
          append
            ? mergeAnimationPages(currentAnimations, result.animations || [])
            : result.animations || []
        );
        setHasMore(Boolean(result.hasMore));
        setNextPageToken(result.nextPageToken);
        setTotalItems(result.totalItems || 0);
        setIsLoaded(true);
      } catch (requestError) {
        if (!isCanceledRequest(requestError)) {
          setError(requestError);
          setHasMore(false);
          setIsLoaded(true);
        }
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [abortCurrentRequest, getStudioAnimations]
  );

  const search = useCallback(
    (value) => {
      const trimmedSearchTerm = typeof value === 'string' ? value.trim() : '';
      setSearchTerm(trimmedSearchTerm);
      setNextPageToken(1);
      fetchAnimations({
        nextSearchTerm: trimmedSearchTerm,
        pageToken: 1,
      });
    },
    [fetchAnimations]
  );

  const loadMore = useCallback(() => {
    if (!hasMore || !nextPageToken || isLoading) {
      return;
    }

    fetchAnimations({
      nextSearchTerm: searchTerm,
      pageToken: nextPageToken,
      append: true,
    });
  }, [fetchAnimations, hasMore, isLoading, nextPageToken, searchTerm]);

  const retry = useCallback(() => {
    fetchAnimations(lastRequestRef.current);
  }, [fetchAnimations]);

  useEffect(() => {
    fetchAnimations();

    return abortCurrentRequest;
  }, [abortCurrentRequest, fetchAnimations]);

  return {
    animations,
    error,
    hasMore,
    isLoaded,
    isLoading,
    loadMore,
    retry,
    search,
    searchTerm,
    totalItems,
  };
}

export default useLottieLibrary;
